/**
 * TeamPage - Our Team page with member profile cards.
 *
 * TO CUSTOMIZE: edit TEAM_OVERVIEW and TEAM_MEMBERS below.
 *  - Profile photos go in  public/images/team/  as .jpg, .png, or .svg
 *    (photoSrc paths below are extensionless; the card tries each format).
 *  - Resume PDFs go in     public/resumes/      (see resumeHref paths).
 * Until a photo is added, the card shows an initials avatar.
 */
import React, { useEffect } from 'react';
import SectionTitleCard from '../components/cards/SectionTitleCard';
import TeamMemberCard, { TeamMemberCardProps } from '../components/cards/TeamMemberCard';
import BookingCTACard from '../components/cards/BookingCTACard';

const TEAM_OVERVIEW =
  'Thornless Data Management is a service of Kuiken Technologies LLC. The team is comprised of software and data engineers with over a decade of combined experience. When you work with us, you work directly with the people designing and building your solution — no layers of account managers, no hand-offs to junior staff. We bring experience across cloud data platforms, analytics, and AI automation, and we measure our success by the outcomes we deliver.';

const TEAM_MEMBERS: TeamMemberCardProps[] = [
  {
    name: 'Adam Kuiken',
    role: 'Founder & Principal Data Engineer',
    photoSrc: '/images/team/adam-kuiken',
    photoZoom: 1.2,
    photoShiftX: 9,
    bio: 'Adam founded Kuiken Technologies to help businesses build sustainable technology. He created Thornless Data Management as a way to focus that technology on data and give businesses access to enterprise-grade data engineering without enterprise overhead.',
    resumeHref: '/resumes/adam-kuiken-resume.pdf',
    resumeLabel: 'View Resume',
  },
  {
    name: 'Coleman Sitzman',
    role: 'Principal Data Analyst',
    photoSrc: '/images/team/team-member-2-centered',
    bio: 'Coleman is an analytical and business-focused Data Analyst with 5 years of experience delivering analytics solutions across retail banking, commercial banking, private wealth, marketing, and product teams.',
    resumeHref: '/resumes/team-member-2-resume.pdf',
    resumeLabel: 'View Resume',
  },
];

const TeamPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Our Team | Thornless Data Management';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Meet the engineers behind Thornless Data Management — the senior team designing and building your data pipelines, analytics, and AI automation.');
  }, []);

  return (
    <main>
      <section className="layout-container section-padding" aria-label="OurTeam">
        <SectionTitleCard eyebrow={'Who We Are'} title={'Our Team'} subtitle={TEAM_OVERVIEW} />
        <div className="grid-2" style={{ maxWidth: '880px', margin: '0 auto' }}>
          {TEAM_MEMBERS.map((member) => (
            <TeamMemberCard key={member.name} {...member} />
          ))}
        </div>
      </section>
      <section className="layout-container section-padding" aria-label="TeamCTA">
        <BookingCTACard
          headline={'Work Directly With Our Engineers.'}
          description={'Book a free Discovery call and talk through your data challenges with the people who will actually build your solution.'}
          cta_label={'Book a Discovery Call'}
          cta_href={'/contact'}
        />
      </section>
    </main>
  );
};

export default TeamPage;
